import { useState } from "react";
import { LifeBuoy, Pencil, Phone, Link2, Plus, X, Check, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { VignetteAdvice } from "./VignetteAdvice";

const SUGGESTIES = ["Pechbijstand", "Assistentie wagen", "Verzekering (polis)", "Verzekeringsmakelaar", "Europees noodnummer"];

const isTel = (v) => /^[+0-9][0-9 ()./-]{4,}$/.test((v || "").trim());
const isUrl = (v) => /^(https?:\/\/|www\.)/i.test((v || "").trim());

const ValueDisplay = ({ value }) => {
  const v = (value || "").trim();
  if (isTel(v)) {
    return (
      <a href={`tel:${v.replace(/[^+0-9]/g, "")}`} className="font-mono font-semibold text-primary hover:underline flex items-center gap-1.5">
        <Phone className="w-3.5 h-3.5 shrink-0" /> {v}
      </a>
    );
  }
  if (isUrl(v)) {
    return (
      <a href={v.startsWith("http") ? v : `https://${v}`} target="_blank" rel="noreferrer" className="font-semibold text-[#3A6EA5] hover:underline flex items-center gap-1.5 break-all">
        <Link2 className="w-3.5 h-3.5 shrink-0" /> {v.replace(/^https?:\/\//, "")}
      </a>
    );
  }
  return <span className="font-semibold">{v}</span>;
};

export const ImportantInfo = ({ trip, onRefresh, countries }) => {
  const canEdit = trip.is_owner !== false;
  const items = trip.important_info || [];
  const [open, setOpen] = useState(items.length > 0);
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const startEdit = () => {
    setRows(items.length ? items.map((i) => ({ ...i })) : [{ label: "", value: "" }]);
    setEditing(true);
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/trips/${trip.id}/important-info`, { items: rows });
      toast.success("Belangrijke info opgeslagen");
      setEditing(false);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!canEdit && !items.length && !(countries || []).length) return null;

  return (
    <div data-testid="important-info-card" className="bg-card rounded-2xl mb-4 overflow-hidden">
      <button data-testid="important-info-toggle" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 p-4 text-left hover:bg-secondary/50 transition-[background-color] duration-200">
        <LifeBuoy className="w-4 h-4 text-[#B5838D] shrink-0" />
        <span className="font-heading font-bold text-sm flex-1">Belangrijke info</span>
        {items.length > 0 && <span className="text-xs text-muted-foreground font-mono">{items.length}</span>}
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          <VignetteAdvice countries={countries} />
          {!editing && items.length === 0 && (
            <p className="text-xs text-muted-foreground">Bewaar hier alles wat je onderweg snel nodig kan hebben: pechbijstand, het assistentienummer van je wagen, de link naar je verzekeringspolis of je makelaar.</p>
          )}

          {!editing && items.map((it, i) => (
            <div key={i} data-testid={`important-info-row-${i}`} className="flex items-baseline justify-between gap-3 text-sm border-b border-border/40 pb-2 last:border-0 last:pb-0">
              <span className="text-muted-foreground shrink-0">{it.label}</span>
              <ValueDisplay value={it.value} />
            </div>
          ))}

          {editing && (
            <>
              {rows.map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input data-testid={`important-info-label-${i}`} value={r.label} placeholder="Label (bv. Pechbijstand)"
                    onChange={(e) => setRows((rs) => rs.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                    className="h-10 rounded-xl text-sm flex-[2]" list="important-info-suggesties" />
                  <Input data-testid={`important-info-value-${i}`} value={r.value} placeholder="Nummer, link of tekst"
                    onChange={(e) => setRows((rs) => rs.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
                    className="h-10 rounded-xl text-sm flex-[3]" />
                  <button data-testid={`important-info-remove-${i}`} type="button" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                    className="p-1.5 rounded-full hover:bg-red-500/10 text-muted-foreground hover:text-red-500 transition-[background-color,color] duration-150 shrink-0">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <datalist id="important-info-suggesties">
                {SUGGESTIES.map((s) => <option key={s} value={s} />)}
              </datalist>
              <div className="flex flex-wrap gap-1.5">
                {SUGGESTIES.filter((s) => !rows.some((r) => r.label === s)).map((s) => (
                  <button key={s} type="button" data-testid={`important-info-suggestie-${s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                    onClick={() => setRows((rs) => [...rs.filter((r) => r.label || r.value), { label: s, value: s === "Europees noodnummer" ? "112" : "" }])}
                    className="text-[11px] font-semibold rounded-full border border-dashed border-primary/40 text-primary px-2.5 py-1 hover:border-primary transition-[border-color] duration-150">
                    + {s}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Button data-testid="important-info-add-row" type="button" variant="outline" size="sm" onClick={() => setRows((rs) => [...rs, { label: "", value: "" }])}
                  className="rounded-full h-8 text-xs">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Rij toevoegen
                </Button>
                <div className="flex-1" />
                <Button data-testid="important-info-cancel" type="button" variant="ghost" size="sm" onClick={() => setEditing(false)} className="rounded-full h-8 text-xs">
                  Annuleren
                </Button>
                <Button data-testid="important-info-save" type="button" size="sm" onClick={save} disabled={busy}
                  className="rounded-full h-8 text-xs bg-primary hover:bg-[#1B4332]">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />} Opslaan
                </Button>
              </div>
            </>
          )}

          {!editing && canEdit && (
            <Button data-testid="important-info-edit" variant="outline" size="sm" onClick={startEdit} className="rounded-full h-8 text-xs">
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> {items.length ? "Bewerken" : "Info toevoegen"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
